import { JsonObject, JsonProperty } from "typescript-json-serializer";

type DateProperties = number | string | Date;

@JsonObject()
export class TimeExtentConfiguration {
    /**
     * The end time of the time extent.
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-TimeExtent.html#end)
     */
    @JsonProperty()
    declare end: DateProperties;
    /**
     * The start time of the time extent.
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-TimeExtent.html#start)
     */
    @JsonProperty()
    declare start: DateProperties;

    constructor() {}
}

@JsonObject()
export class TimeIntervalConfiguration {
    /**
     * Temporal units.
     *
     * @default milliseconds
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-TimeInterval.html#unit)
     */
    @JsonProperty()
    declare unit?:
    | "milliseconds"
    | "seconds"
    | "minutes"
    | "hours"
    | "days"
    | "weeks"
    | "months"
    | "years"
    | "decades"
    | "centuries";

    /**
     * The numerical value of the time extent.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-TimeInterval.html#value)
     */
    @JsonProperty()
    declare value?: number;

    constructor() {}
}

@JsonObject()
export class TimeInfoConfiguration {
    /**
     * The name of the field containing the end time information.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-support-TimeInfo.html#endField)
     */
    @JsonProperty()
    declare endField?: string;

    /**
     * The time extent defines the start time and end time for all data in the layer.
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-support-TimeInfo.html#fullTimeExtent)
     */
    @JsonProperty()
    declare fullTimeExtent?: TimeExtentConfiguration;

    /**
     * The time interval defines the granularity of the temporal data and allows you to visualize the data at specified intervals using the [time slider widget](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-TimeSlider.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-support-TimeInfo.html#interval)
     */
    @JsonProperty()
    declare interval?: TimeIntervalConfiguration;

    /**
     * The name of the field containing the start time information.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-support-TimeInfo.html#startField)
     */
    @JsonProperty()
    declare startField?: string;

    /**
     * The IANA time zone that dates are stored in.
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-support-TimeInfo.html#timeZone)
     */
    @JsonProperty()
    declare timeZone?: string;

    /**
     * The name of the field used to join or group discrete locations.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-support-TimeInfo.html#trackIdField)
     */
    @JsonProperty()
    declare trackIdField: string;

    constructor() {}
}
